import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AuthUser {
    id: number;
    fullName: string;
    email: string;
    currentPlan?: string;
    planExpiry?: string;
}

interface AuthContextType {
    user: AuthUser | null;
    token: string | null;
    isLoggedIn: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
    register: (fullName: string, email: string, password: string) => Promise<{ success: boolean; message?: string }>;
    logout: () => void;
    updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null, token: null, isLoggedIn: false,
    login: async () => ({ success: false }),
    register: async () => ({ success: false }),
    logout: () => {},
    updateUser: () => {},
});

//const SUBS_URL = 'http://localhost:5002/api';
const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';


export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<AuthUser | null>(() => {
        try { const s = localStorage.getItem('sub_user'); return s ? JSON.parse(s) : null; }
        catch { return null; }
    });
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('sub_token'));

    useEffect(() => {
        // ✅ Logout event
        const handleLogout = () => {
            setToken(null);
            setUser(null);
        };
        // ✅ Login event — RegisterPage/LoginPage direct localStorage set karte hain
        // Ye event sunke state update ho jata hai — reload ki zaroorat nahi
        const handleLogin = () => {
            try {
                const t = localStorage.getItem('sub_token');
                const u = localStorage.getItem('sub_user');
                if (t) setToken(t);
                if (u) setUser(JSON.parse(u));
            } catch {}
        };
        window.addEventListener('auth:logout', handleLogout);
        window.addEventListener('auth:login', handleLogin);
        return () => {
            window.removeEventListener('auth:logout', handleLogout);
            window.removeEventListener('auth:login', handleLogin);
        };
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const res = await fetch(`${SUBS_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('sub_token', data.token);
                localStorage.setItem('sub_user', JSON.stringify(data.user));
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            }
            return { success: false, message: data.message || 'Login failed' };
        } catch {
            return { success: false, message: 'Server error. Try again.' };
        }
    };

    const register = async (fullName: string, email: string, password: string) => {
        try {
            const res = await fetch(`${SUBS_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fullName, email, password }),
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('sub_token', data.token);
                localStorage.setItem('sub_user', JSON.stringify(data.user));
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            }
            return { success: false, message: data.message || 'Registration failed' };
        } catch {
            return { success: false, message: 'Server error. Try again.' };
        }
    };

    const logout = () => {
        localStorage.removeItem('sub_token');
        localStorage.removeItem('sub_user');
        setToken(null);
        setUser(null);
    };

    const updateUser = (updatedUser: AuthUser) => {
        setUser(updatedUser);
        localStorage.setItem('sub_user', JSON.stringify(updatedUser));
    };

    return (
        <AuthContext.Provider value={{ user, token, isLoggedIn: !!token, login, register, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);